/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Row, Spin } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { onError } from 'utils/handlers';
import { checkToken } from 'api/utils';
import { getEvaluacionGeneralReloj } from 'api/configuraciones/clock/EvaluacionClock/EvaluacionGeneral';
import { getIncidenciasEmpleados } from 'api/nominas/incidencias/incidenciasEmpleados';
import IncidenciaBonificar from './IncidenciaBonificar';

/**
 * Aplana una IncidenciaEmpleado (con su GuardarIncidencias anidado) a un
 * registro plano para la tabla, ya que Table (components/Table) resuelve
 * cada columna con `record[dataIndex]` (sin soporte de rutas anidadas).
 * Conserva además los ids "en crudo" (no mostrados en columnas) que necesita
 * el modal "Bonificar" para crear la Bonificación Empleado.
 */
/** Un registro ya no se puede bonificar cuando TODOS sus Descuentos Empleados
 * ya fueron reembolsados por completo (suma de días de sus Bonificaciones
 * Empleado existentes >= días originalmente descontados). Si no tiene ningún
 * Descuento Empleado ligado, se deja bonificable (el modal ya avisa que no
 * hay nada que reembolsar). */
const esTotalmenteBonificado = (descuentosEmpleados) => !!descuentosEmpleados?.length
  && descuentosEmpleados.every((d) => d.totalmente_bonificado);

const flattenIncidenciaEmpleado = (registros) => (registros || []).map((registro) => {
  const guardarIncidencia = registro.guardar_incidencia || {};
  return {
    id: registro.id,
    empleado_id: guardarIncidencia.empleado?.id,
    empleado_numero: guardarIncidencia.empleado?.numero_de_empleado || '',
    empleado_numero_y_nombre_completo: guardarIncidencia.empleado?.numero_y_nombre_completo || '',
    tipo_de_nomina_empleado_id: guardarIncidencia.empleado?.tipo_de_nomina_empleado,
    directorio_de_area_clave_y_nombre: guardarIncidencia.directorio_de_area?.clave_y_nombre || '',
    puesto_clave_y_descripcion: guardarIncidencia.puesto?.clave_y_descripcion || '',
    plaza_clave_y_descripcion: guardarIncidencia.plaza?.clave_y_descripcion || '',
    tipo_de_evaluacion_de_asistencia_nombre: guardarIncidencia.tipo_de_evaluacion_de_asistencia?.nombre || '',
    numero_incidencias: guardarIncidencia.numero_incidencias ?? '',
    minutos_incidencia: guardarIncidencia.minutos_incidencia ?? '',
    cantidad_descuento: guardarIncidencia.cantidad_descuento ?? '',
    unidad_descuento_descripcion: guardarIncidencia.unidad_descuento?.descripcion || '',
    guardar_incidencia_id: guardarIncidencia.id,
    guardar_incidencia_estatus: guardarIncidencia.estados_globales?.descripcion || '',
    evaluacion_cierre_id: guardarIncidencia.evaluacion_cierre?.id,
    // Fechas exactas (día por día) que originaron la incidencia -- las
    // necesita el modal "Bonificar" para mostrar la fecha de cada día que se
    // está bonificando (ver IncidenciaBonificar.jsx).
    fechas_incidencia: guardarIncidencia.fechas_incidencia || [],
    dias_aplicados_al_100: registro.dias_aplicados_al_100,
    dias_aplicados_al_50: registro.dias_aplicados_al_50,
    dias_aplicados_al_0: registro.dias_aplicados_al_0,
    periodos: registro.periodos || '',
    estatus: registro.estados_globales?.descripcion || '',
    descuentos_empleados: registro.descuentos_empleados || [],
    totalmente_bonificado: esTotalmenteBonificado(registro.descuentos_empleados),
  };
});

const IncidenciaDescuento = ({ permission, parametroBusqueda, activeTabKey }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [visibleBonificar, setVisibleBonificar] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const evaluacion = await getEvaluacionGeneralReloj(parametroBusqueda);
      const evaluacionId = evaluacion?.results ? evaluacion.results[0]?.id : null;
      if (!evaluacionId) {
        setData([]);
        return;
      }
      const _data = await getIncidenciasEmpleados({
        guardar_incidencia_evaluacion_cierre: evaluacionId,
      });
      setData(flattenIncidenciaEmpleado(_data?.results || _data));
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  // Este tab convive con las demás pestañas de Incidencias vía `forceRender`
  // (todas se montan de inmediato, ocultas con CSS), así que un fetch en el
  // montaje inicial no vuelve a dispararse al cambiar de pestaña. Se refresca
  // cada vez que el usuario entra a esta pestaña para reflejar las
  // incidencias recién autorizadas en el tab "Autorizar".
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      if (activeTabKey !== 'Descuentos') {
        return;
      }
      setLoading(true);
      await checkToken();
      if (mounted) {
        await fetchData();
      }
      setLoading(false);
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [activeTabKey]);

  const columns = [
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado_numero',
      key: 'empleado_numero',
      width: 150,
    },
    {
      titleText: 'Nombre del Empleado',
      dataIndex: 'empleado_numero_y_nombre_completo',
      key: 'empleado_numero_y_nombre_completo',
      width: 280,
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area_clave_y_nombre',
      key: 'directorio_de_area_clave_y_nombre',
      width: 300,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_clave_y_descripcion',
      key: 'puesto_clave_y_descripcion',
      width: 260,
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plaza_clave_y_descripcion',
      key: 'plaza_clave_y_descripcion',
      width: 260,
    },
    {
      titleText: 'Tipo de Evaluación de Asistencia',
      dataIndex: 'tipo_de_evaluacion_de_asistencia_nombre',
      key: 'tipo_de_evaluacion_de_asistencia_nombre',
      width: 260,
    },
    {
      titleText: 'No. de Incidencias',
      dataIndex: 'numero_incidencias',
      key: 'numero_incidencias',
      width: 160,
    },
    {
      titleText: 'Minutos de Incidencia',
      dataIndex: 'minutos_incidencia',
      key: 'minutos_incidencia',
      width: 190,
    },
    {
      titleText: 'Cantidad de Descuento',
      dataIndex: 'cantidad_descuento',
      key: 'cantidad_descuento',
      width: 190,
    },
    {
      titleText: 'Unidad de Descuento',
      dataIndex: 'unidad_descuento_descripcion',
      key: 'unidad_descuento_descripcion',
      width: 190,
    },
    {
      titleText: 'Días Aplicados al 100%',
      dataIndex: 'dias_aplicados_al_100',
      key: 'dias_aplicados_al_100',
      width: 170,
    },
    {
      titleText: 'Días Aplicados al 50%',
      dataIndex: 'dias_aplicados_al_50',
      key: 'dias_aplicados_al_50',
      width: 170,
    },
    {
      titleText: 'Días Aplicados al 0%',
      dataIndex: 'dias_aplicados_al_0',
      key: 'dias_aplicados_al_0',
      width: 170,
    },
    {
      titleText: 'Periodos de Nómina',
      dataIndex: 'periodos',
      key: 'periodos',
      width: 260,
    },
    {
      titleText: 'Estatus Guardar Incidencia',
      dataIndex: 'guardar_incidencia_estatus',
      key: 'guardar_incidencia_estatus',
      width: 190,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus',
      key: 'estatus',
      width: 160,
    },
    {
      titleText: 'Estatus de Bonificación',
      dataIndex: 'totalmente_bonificado',
      key: 'totalmente_bonificado',
      width: 180,
      render: (val) => (val ? 'Ya Bonificado' : 'Pendiente'),
    },
  ];

  // La casilla de selección de cada fila no es clickeable: hay una regla CSS
  // global (Layout/index.scss, "tbody .ant-table-selection-column label")
  // que le pone pointer-events: none a propósito. En todo el proyecto la
  // selección real se hace con handleOnRowClick (clic en cualquier parte de
  // la fila) — ver IncidenciaAutorizar.jsx — y `rowSelection` solo sirve
  // para reflejar visualmente cuáles filas quedaron marcadas. Selección
  // múltiple: cada clic agrega o quita esa fila del conjunto seleccionado.
  const handleOnRowClick = (record) => {
    // Un registro ya totalmente bonificado no se puede volver a seleccionar
    // para bonificar (ver `esTotalmenteBonificado` más arriba).
    if (record.totalmente_bonificado) {
      return;
    }
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id));
      setSelectedData(selectedData.filter((row) => row.id !== record.id));
    } else {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
      setSelectedData([...selectedData, record]);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    getCheckboxProps: (record) => ({
      disabled: record.totalmente_bonificado,
    }),
  };

  const onCancelBonificar = () => {
    setVisibleBonificar(false);
  };

  const onSuccessBonificar = async () => {
    setVisibleBonificar(false);
    setSelectedRowKeys([]);
    setSelectedData([]);
    await fetchData();
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          customActions={[
            {
              onClick: () => setVisibleBonificar(true),
              icon: <GiftOutlined />,
              text: 'Bonificar',
              className: 'success',
            },
          ]}
          permission={permission}
          allowExportPDF
        />
        <IncidenciaBonificar
          open={visibleBonificar}
          records={selectedData}
          onCancel={onCancelBonificar}
          onSuccess={onSuccessBonificar}
        />
      </Spin>
    </Row>
  );
};

export default IncidenciaDescuento;
